// EF-182 — Proactive Supabase session keep-alive.
//
// Brandon's hard requirement: when Erevis is open, an order must always be
// executable regardless of how long the tab has been idle. Supabase JS's
// internal auto-refresh covers many cases but is throttled when the tab is
// backgrounded and can fall behind in long-idle scenarios.
//
// This hook layers two explicit refreshes on top of Supabase's defaults:
//   1. A 15-minute heartbeat: refreshSession() fires on a setInterval while
//      the user is signed in.
//   2. A visibilitychange listener: every transition to visible fires a
//      refreshSession() immediately, so the access_token is rotated the
//      moment Brandon returns to the Erevis tab.
//
// Mount once at the app root inside AuthProvider — see App.jsx wiring.
//
// Failed refreshes are swallowed here. The reactive recovery path
// (useOrderGateway's SESSION_EXPIRED silent retry, EF-182 Section B) is
// the source of truth for "the order must go through."

import { useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

const HEARTBEAT_MS = 15 * 60 * 1000

export function useSessionKeepAlive() {
  const { user } = useAuth()
  const userId = user?.id

  useEffect(() => {
    if (!userId) return

    const safeRefresh = () => {
      supabase.auth.refreshSession().catch(() => {
        // Reactive recovery in useOrderGateway is authoritative — see header.
      })
    }

    const intervalId = setInterval(safeRefresh, HEARTBEAT_MS)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') safeRefresh()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [userId])
}

// EF-182 — null-rendering mount point. Lives as a child of <AuthProvider>
// in App.jsx so useAuth() resolves correctly inside the hook.
export function SessionKeepAlive() {
  useSessionKeepAlive()
  return null
}
